import React, { useState } from 'react';
import { 
  MousePointer2, 
  Monitor, 
  Laptop, 
  Zap, 
  ShieldCheck, 
  Layers, 
  ArrowRight, 
  CheckCircle2, 
  Download, 
  Sparkles, 
  Lock, 
  Unlock, 
  Gamepad2, 
  Cpu, 
  Wifi, 
  Activity, 
  FolderDown, 
  ExternalLink,
  ChevronRight,
  HelpCircle,
  X,
  Github,
  MessageSquare,
  Check,
  Globe,
  Award,
  CreditCard
} from 'lucide-react';
import { PurchaseFlowModal } from './PurchaseFlowModal';
import { CURRENT_APP_VERSION } from '../utils/updateChecker';

interface FlowLandingPageProps {
  onOpenDownloads: () => void;
  onOpenGuide: (tab?: 'android' | 'windows' | 'security') => void;
  onSwitchToShare: () => void;
}

export const FlowLandingPage: React.FC<FlowLandingPageProps> = ({
  onOpenDownloads,
  onOpenGuide,
  onSwitchToShare
}) => {
  const [activeScreen, setActiveScreen] = useState<0 | 1>(0);
  const [cursorX, setCursorX] = useState(25);
  const [cursorY, setCursorY] = useState(50);
  const [isLocked, setIsLocked] = useState(false);
  const [latency, setLatency] = useState(0.8);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [purchasePlan, setPurchasePlan] = useState<'ANNUAL' | 'LIFETIME'>('LIFETIME');

  const openPurchase = (plan: 'ANNUAL' | 'LIFETIME' = 'LIFETIME') => {
    setPurchasePlan(plan);
    setIsPurchaseModalOpen(true);
  };

  const handleOpenWhatsAppDirect = (planText: string = 'Lifetime PRO ($29.99)') => {
    const waMsg = encodeURIComponent(
      `Hi Chintan! I'd like to purchase CK Flow PRO (${planText}).\nPlease share payment details and generate my official license key!`
    );
    window.open(`https://wa.me/917600477225?text=${waMsg}`, '_blank', 'noopener,noreferrer');
  };

  // Handle interactive glide across screens in demo box
  const handleDemoMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const xPct = ((e.clientX - rect.left) / rect.width) * 100;
    const yPct = ((e.clientY - rect.top) / rect.height) * 100;

    if (isLocked) {
      setCursorX(Math.min(xPct, 49));
    } else {
      setCursorX(xPct);
      if (xPct >= 50) {
        setActiveScreen(1);
      } else {
        setActiveScreen(0);
      }
    }
    setCursorY(yPct);
  };

  return (
    <div className="w-full text-slate-100 overflow-hidden font-sans">
      
      {/* 1. TOP ANNOUNCEMENT BAR */}
      <div className="w-full bg-gradient-to-r from-cyan-950/80 via-purple-950/80 to-black border-b border-cyan-500/20 py-2 px-4 text-center text-xs">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 flex-wrap">
          <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 font-bold font-mono text-[10px]">
            LOCAL LAN: 100% FREE FOREVER
          </span>
          <span className="text-slate-300">
            <strong>Remote Internet Mesh:</strong> 1-Year Pass for <strong>$9.99</strong> or Lifetime Freedom for <strong>$29.99</strong>.
          </span>
          <button
            onClick={() => openPurchase('LIFETIME')}
            className="text-cyan-400 hover:text-cyan-300 font-bold inline-flex items-center gap-1 underline underline-offset-2 transition-colors cursor-pointer"
          >
            <span>Upgrade to Remote PRO ➔</span>
          </button>
        </div>
      </div>

      {/* 2. MAIN FLOW NAVBAR */}
      <nav className="sticky top-0 z-40 w-full bg-[#070912]/85 backdrop-blur-xl border-b border-white/10 px-4 sm:px-6 lg:px-8 py-3.5 transition-all">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          
          {/* Brand Logo */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-cyan-500 to-purple-600 p-0.5 shadow-[0_0_20px_rgba(0,240,255,0.4)] flex items-center justify-center">
              <div className="w-full h-full bg-[#070912] rounded-[14px] flex items-center justify-center text-cyan-400">
                <MousePointer2 size={20} className="transform -rotate-45" />
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-extrabold text-white font-display tracking-tight">
                  CK <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-teal-300 to-purple-400">Flow</span>
                </h1>
                <span className="px-2 py-0.5 text-[9px] font-bold font-mono text-cyan-300 bg-cyan-500/15 border border-cyan-500/30 rounded-full">
                  MULTI-PC MESH
                </span>
              </div>
              <p className="text-[10px] text-slate-400 font-mono hidden sm:block">1 Mouse &amp; Keyboard • Multiple Computers</p>
            </div>
          </div>

          {/* Nav Actions */}
          <div className="flex items-center gap-3">
            <a
              href="#pricing"
              className="hidden sm:inline-block px-3 py-1.5 text-xs font-semibold text-cyan-300 hover:text-white transition-colors"
            >
              Pricing ($9.99 / $29.99)
            </a>

            <a
              href="#simulator"
              className="hidden md:inline-block px-3 py-1.5 text-xs font-semibold text-slate-300 hover:text-white transition-colors"
            >
              Live Demo
            </a>

            <button
              onClick={() => handleOpenWhatsAppDirect()}
              className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 transition-colors cursor-pointer"
            >
              <MessageSquare size={13} />
              <span>WhatsApp: +91 7600477225</span>
            </button>

            <button
              onClick={onSwitchToShare}
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors cursor-pointer"
            >
              <span>📱 CK Share Web App</span>
            </button>

            <button
              onClick={onOpenDownloads}
              className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-black bg-gradient-to-r from-cyan-400 to-emerald-400 hover:from-cyan-300 hover:to-emerald-300 rounded-xl shadow-[0_0_20px_rgba(0,240,255,0.3)] hover:scale-105 transition-all cursor-pointer"
            >
              <Download size={14} />
              <span>Free Download</span>
            </button>
          </div>

        </div>
      </nav>

      {/* 3. HERO SECTION */}
      <section className="relative pt-16 pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
        
        {/* Glow Spheres */}
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-96 h-96 bg-cyan-500/15 rounded-full blur-3xl pointer-events-none -z-10" />
        <div className="absolute top-40 left-1/3 w-80 h-80 bg-purple-600/15 rounded-full blur-3xl pointer-events-none -z-10" />

        {/* Hero Tag */}
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-black border border-cyan-500/30 text-cyan-300 text-xs font-semibold mb-6 shadow-inner animate-fadeIn">
          <Zap size={14} className="text-cyan-400 animate-pulse" />
          <span>Local Wi-Fi Mesh is 100% Free • Remote Internet Pro Available</span>
        </div>

        {/* Main Headline */}
        <h2 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold text-white font-display tracking-tight leading-[1.1] max-w-5xl mx-auto">
          One Mouse &amp; Keyboard. <br className="hidden sm:block" />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-teal-300 to-purple-400">
            Glide Seamlessly
          </span> Across All Your PCs.
        </h2>

        {/* Subtitle */}
        <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-3xl mx-auto leading-relaxed">
          Move your mouse off the edge of your Windows laptop, and the cursor instantly appears on your desktop screen. 
          Use local LAN for <strong>free</strong>, or control computers across remote internet with <strong>CK Flow PRO</strong>.
        </p>

        {/* Action Buttons */}
        <div className="mt-8 flex items-center justify-center gap-4 flex-wrap">
          <a
            href="/downloads/CKFlow.exe"
            download="CKFlow.exe"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 px-7 py-3.5 text-sm font-bold text-black bg-gradient-to-r from-cyan-400 to-emerald-400 hover:from-cyan-300 hover:to-emerald-300 rounded-2xl shadow-[0_0_30px_rgba(0,240,255,0.4)] hover:shadow-[0_0_40px_rgba(0,240,255,0.6)] hover:scale-105 transition-all cursor-pointer"
          >
            <Download size={18} />
            <span>Download CK Flow (.exe)</span>
          </a>

          <button
            onClick={() => openPurchase('LIFETIME')}
            className="flex items-center gap-2 px-6 py-3.5 text-sm font-bold text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 rounded-2xl shadow-[0_0_25px_rgba(168,85,247,0.35)] transition-all cursor-pointer hover:scale-105"
          >
            <Sparkles size={18} className="text-purple-300" />
            <span>Get Remote PRO ($9.99 / $29.99)</span>
          </button>
        </div>

        {/* Quick Trust Badges */}
        <div className="mt-10 flex items-center justify-center gap-6 sm:gap-8 flex-wrap text-xs text-slate-400">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={15} className="text-emerald-400" />
            <span>Local Mesh: <strong>Free Forever</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 size={15} className="text-cyan-400" />
            <span>Sub-1ms Unicast UDP</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 size={15} className="text-purple-400" />
            <span>Remote Internet Mesh: <strong>$9.99/yr or $29.99 Lifetime</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 size={15} className="text-cyan-400" />
            <span>WhatsApp Support: <strong>+91 7600477225</strong></span>
          </div>
        </div>

      </section>

      {/* 4. INTERACTIVE LIVE DESK SIMULATOR SECTION */}
      <section id="simulator" className="py-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        
        <div className="p-6 sm:p-8 rounded-3xl bg-gradient-to-b from-[#101426]/90 via-[#0a0d1a]/95 to-[#060810] border border-cyan-500/30 shadow-[0_25px_80px_rgba(0,0,0,0.9),0_0_50px_rgba(0,240,255,0.15)] space-y-6 relative overflow-hidden">
          
          {/* Section Header */}
          <div className="flex items-center justify-between flex-wrap gap-4 border-b border-white/10 pb-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-ping" />
                <h3 className="text-base sm:text-lg font-bold text-white font-display">
                  Interactive Multi-Screen Gliding Simulator
                </h3>
              </div>
              <p className="text-xs text-slate-400">Hover and drag your mouse inside the desk area to test seamless cursor edge handoff</p>
            </div>

            <div className="flex items-center gap-3 text-xs">
              <button
                onClick={() => setIsLocked(!isLocked)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border transition-all cursor-pointer ${
                  isLocked 
                    ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                    : 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30'
                }`}
              >
                {isLocked ? <Lock size={13} /> : <Unlock size={13} />}
                <span>{isLocked ? '🔒 Locked to PC 1 (Ctrl+Ctrl)' : '🔓 Flow Active (Unlocked)'}</span>
              </button>

              <span className="px-3 py-1.5 rounded-xl bg-emerald-950/40 border border-emerald-500/30 text-emerald-300 font-mono font-bold">
                ⚡ {latency}ms Transit Latency
              </span>
            </div>
          </div>

          {/* Interactive Dual Screen Surface */}
          <div 
            onMouseMove={handleDemoMouseMove}
            className="w-full h-80 rounded-2xl bg-[#04060d] border border-white/10 relative overflow-hidden cursor-crosshair select-none p-4 grid grid-cols-2 gap-4"
          >
            {/* Center Edge Boundary Line */}
            <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-0.5 bg-gradient-to-b from-cyan-400 via-purple-500 to-cyan-400 z-10 opacity-70">
              <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 left-1/2 px-2 py-0.5 rounded-full bg-cyan-500 text-[9px] font-bold text-black whitespace-nowrap shadow-lg">
                Seamless Edge Handoff
              </div>
            </div>

            {/* SCREEN 1: Primary PC (Left) */}
            <div className={`rounded-xl border p-4 flex flex-col justify-between transition-all ${
              activeScreen === 0 
                ? 'bg-cyan-950/20 border-cyan-500/50 shadow-[0_0_30px_rgba(0,240,255,0.15)]' 
                : 'bg-white/[0.02] border-white/5 opacity-60'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Laptop size={18} className="text-cyan-400" />
                  <span className="text-xs font-bold text-white">Laptop (Host PC)</span>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono font-bold ${
                  activeScreen === 0 ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40' : 'text-slate-500'
                }`}>
                  {activeScreen === 0 ? 'ACTIVE CONTROL' : 'STANDBY'}
                </span>
              </div>

              <div className="text-center space-y-1">
                <p className="text-xs text-slate-300">Physical Mouse &amp; Keyboard Connected</p>
                <p className="text-[11px] text-slate-500">1920 × 1080 • Primary Display</p>
              </div>

              <div className="text-[10px] text-cyan-400/80 font-mono text-right">
                Push right ➔ to enter Desktop
              </div>
            </div>

            {/* SCREEN 2: Secondary PC (Right) */}
            <div className={`rounded-xl border p-4 flex flex-col justify-between transition-all ${
              activeScreen === 1 
                ? 'bg-purple-950/20 border-purple-500/50 shadow-[0_0_30px_rgba(168,85,247,0.15)]' 
                : 'bg-white/[0.02] border-white/5 opacity-60'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Monitor size={18} className="text-purple-400" />
                  <span className="text-xs font-bold text-white">Workstation (2nd PC)</span>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono font-bold ${
                  activeScreen === 1 ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40' : 'text-slate-500'
                }`}>
                  {activeScreen === 1 ? 'RECEIVING INPUT' : 'STANDBY'}
                </span>
              </div>

              <div className="text-center space-y-1">
                <p className="text-xs text-slate-300">Hardware Cursor Forwarded</p>
                <p className="text-[11px] text-slate-500">2560 × 1440 • Secondary Display</p>
              </div>

              <div className="text-[10px] text-purple-400/80 font-mono text-left">
                Push left ➔ to return to Laptop
              </div>
            </div>

            {/* Simulated Live Cursor Indicator */}
            <div 
              className="absolute pointer-events-none z-30 transition-transform duration-75"
              style={{
                left: `${cursorX}%`,
                top: `${cursorY}%`,
                transform: 'translate(-2px, -2px)'
              }}
            >
              <div className="relative">
                <MousePointer2 
                  size={22} 
                  className={`drop-shadow-[0_0_10px_rgba(0,240,255,0.8)] ${
                    activeScreen === 0 ? 'text-cyan-300' : 'text-purple-300'
                  }`} 
                />
                <span className="absolute left-6 top-0 px-1.5 py-0.5 rounded bg-black/80 border border-white/20 text-[9px] font-mono text-white whitespace-nowrap">
                  {activeScreen === 0 ? 'PC 1 (Local)' : 'PC 2 (Remote)'}
                </span>
              </div>
            </div>

          </div>

          {/* Simulator Footer Explanation */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 text-xs text-slate-400">
            <div className="flex items-start gap-2.5">
              <Zap size={16} className="text-cyan-400 flex-shrink-0 mt-0.5" />
              <div>
                <strong className="text-white">Zero Edge Hang:</strong> Infinite delta trapping keeps cursor moving forward smoothly without stopping at borders.
              </div>
            </div>
            <div className="flex items-start gap-2.5">
              <Wifi size={16} className="text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <strong className="text-white">Direct Unicast UDP:</strong> Bypasses Wi-Fi router broadcast rate-limits for ultra-responsive 144Hz control.
              </div>
            </div>
            <div className="flex items-start gap-2.5">
              <Gamepad2 size={16} className="text-purple-400 flex-shrink-0 mt-0.5" />
              <div>
                <strong className="text-white">Double-Tap Ctrl Lock:</strong> Keep your mouse locked to PC 1 during intense gaming sessions with a quick double-tap.
              </div>
            </div>
          </div>

        </div>

      </section>

      {/* 5. PRICING & EDITIONS SECTION */}
      <section id="pricing" className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-white/5">
        
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="text-xs font-bold text-cyan-400 uppercase tracking-widest font-mono">
            TRANSPARENT &amp; FAIR PRICING
          </span>
          <h3 className="text-3xl sm:text-4xl font-extrabold text-white font-display mt-2">
            Local Mesh is Free. Upgrade for Remote Freedom.
          </h3>
          <p className="text-xs sm:text-sm text-slate-400 mt-2">
            Gliding between computers on your home/office Wi-Fi is 100% free forever. Chargeable PRO tiers enable seamless control over the remote Internet anywhere in the world.
          </p>
        </div>

        {/* 3 Pricing Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          
          {/* TIER 1: Local Mesh (Free) */}
          <div className="p-7 rounded-3xl bg-gradient-to-b from-white/[0.03] to-black/70 border border-white/10 hover:border-emerald-500/40 transition-all flex flex-col justify-between space-y-6 relative">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold">
                  Community Edition
                </span>
                <span className="text-xs font-mono text-slate-400">Local Only</span>
              </div>

              <div>
                <h4 className="text-xl font-bold text-white font-display">Local Wi-Fi Mesh</h4>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold text-white font-display">$0</span>
                  <span className="text-xs text-slate-400 font-semibold">/ Free Forever</span>
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  Complete multi-PC mouse &amp; keyboard sharing across all PCs on your same local Wi-Fi or Ethernet network.
                </p>
              </div>

              <div className="pt-4 border-t border-white/5 space-y-2.5 text-xs text-slate-300">
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-emerald-400 flex-shrink-0" />
                  <span>Unlimited PCs on local Wi-Fi / LAN</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-emerald-400 flex-shrink-0" />
                  <span>Sub-1ms Unicast UDP direct mesh</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-emerald-400 flex-shrink-0" />
                  <span>Infinite Delta Trapping (no edge hang)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-emerald-400 flex-shrink-0" />
                  <span>Double-Tap Ctrl Gaming Lock</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-emerald-400 flex-shrink-0" />
                  <span>Local Clipboard &amp; File QuickDrop</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-emerald-400 flex-shrink-0" />
                  <span>Zero installation standalone Windows app</span>
                </div>
              </div>
            </div>

            <a
              href="/downloads/CKFlow.exe"
              download="CKFlow.exe"
              rel="noopener noreferrer"
              className="w-full py-3 px-4 rounded-xl text-center text-xs font-bold text-white bg-white/10 hover:bg-white/20 border border-white/10 transition-all cursor-pointer block"
            >
              Download CK Flow Free (.exe)
            </a>
          </div>

          {/* TIER 2: Remote 1-Year Pass ($9.99) */}
          <div className="p-7 rounded-3xl bg-gradient-to-b from-cyan-950/20 via-black/70 to-black/90 border border-cyan-500/40 hover:border-cyan-400 transition-all flex flex-col justify-between space-y-6 relative shadow-[0_0_30px_rgba(0,240,255,0.15)]">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="px-3 py-1 rounded-full bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 text-xs font-bold">
                  1-Year Remote Pass
                </span>
                <span className="text-xs font-mono text-cyan-300 font-bold">PRO ANNUAL</span>
              </div>

              <div>
                <h4 className="text-xl font-bold text-white font-display">Remote Internet PRO</h4>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold text-white font-display">$9.99</span>
                  <span className="text-xs text-slate-400 font-semibold">/ year</span>
                </div>
                <p className="text-xs text-slate-300 mt-2">
                  Glide your cursor between computers across different Wi-Fi networks, mobile hotspots, and remote offices.
                </p>
              </div>

              <div className="pt-4 border-t border-white/5 space-y-2.5 text-xs text-slate-300">
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-cyan-400 flex-shrink-0" />
                  <span><strong>Everything in Local Free, plus:</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-cyan-400 flex-shrink-0" />
                  <span>Remote Internet Multi-PC Gliding</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-cyan-400 flex-shrink-0" />
                  <span>Cloudflare Encrypted Relay Fallback</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-cyan-400 flex-shrink-0" />
                  <span>Up to 5 Linked Computers</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-cyan-400 flex-shrink-0" />
                  <span>1-Year Updates &amp; Email Support</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-cyan-400 flex-shrink-0" />
                  <span>Instant HMAC-Signed License Key</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => openPurchase('ANNUAL')}
              className="w-full py-3 px-4 rounded-xl text-center text-xs font-bold text-black bg-gradient-to-r from-cyan-400 to-teal-400 hover:from-cyan-300 hover:to-teal-300 shadow-[0_0_20px_rgba(0,240,255,0.3)] transition-all cursor-pointer"
            >
              Get 1-Year Pass ($9.99)
            </button>
          </div>

          {/* TIER 3: Lifetime Remote Freedom ($29.99) - BEST VALUE */}
          <div className="p-7 rounded-3xl bg-gradient-to-b from-purple-950/40 via-indigo-950/30 to-black border-2 border-purple-500/70 hover:border-purple-400 transition-all flex flex-col justify-between space-y-6 relative shadow-[0_0_40px_rgba(168,85,247,0.3)]">
            
            <div className="absolute -top-3.5 right-6 px-3 py-0.5 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 text-[10px] font-extrabold text-white shadow-lg tracking-wider uppercase">
              ★ BEST VALUE • LIFETIME
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="px-3 py-1 rounded-full bg-purple-500/20 border border-purple-500/40 text-purple-300 text-xs font-bold">
                  Ultimate Freedom
                </span>
                <span className="text-xs font-mono text-purple-300 font-bold">NO SUBSCRIPTIONS</span>
              </div>

              <div>
                <h4 className="text-xl font-bold text-white font-display">Lifetime Remote PRO</h4>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold text-white font-display">$29.99</span>
                  <span className="text-xs text-purple-300 font-semibold">one-time payment</span>
                </div>
                <p className="text-xs text-slate-300 mt-2">
                  Own CK Flow Remote forever. Unlimited PCs, unlimited remote internet access, and zero recurring fees.
                </p>
              </div>

              <div className="pt-4 border-t border-white/5 space-y-2.5 text-xs text-slate-200">
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-purple-400 flex-shrink-0" />
                  <span><strong>Everything in 1-Year PRO, plus:</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-purple-400 flex-shrink-0" />
                  <span><strong>Lifetime Access</strong> (No renewals ever)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-purple-400 flex-shrink-0" />
                  <span><strong>Unlimited Devices &amp; Computers</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-purple-400 flex-shrink-0" />
                  <span>Direct 24/7 VIP WhatsApp &amp; Gmail Support</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-purple-400 flex-shrink-0" />
                  <span>Free lifetime upgrades &amp; macOS/Linux releases</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-purple-400 flex-shrink-0" />
                  <span>Instant delivery via WhatsApp: <strong>+91 7600477225</strong></span>
                </div>
              </div>
            </div>

            <button
              onClick={() => openPurchase('LIFETIME')}
              className="w-full py-3 px-4 rounded-xl text-center text-xs font-extrabold text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 shadow-[0_0_25px_rgba(168,85,247,0.4)] transition-all cursor-pointer hover:scale-105"
            >
              Get Lifetime License ($29.99)
            </button>
          </div>

        </div>

        {/* WhatsApp Instant Support & Purchase Bar */}
        <div className="mt-12 p-6 rounded-3xl bg-gradient-to-r from-emerald-950/40 via-cyan-950/30 to-black border border-emerald-500/30 max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 flex items-center justify-center flex-shrink-0">
              <MessageSquare size={24} />
            </div>
            <div>
              <h4 className="text-sm font-bold text-white">Instant Purchase &amp; Activation via WhatsApp</h4>
              <p className="text-xs text-slate-400">
                Message founder Chintan Kheradia on <strong className="text-emerald-300">+91 7600477225</strong> or email <strong className="text-cyan-300">chintankheradia123@gmail.com</strong>
              </p>
            </div>
          </div>

          <button
            onClick={() => handleOpenWhatsAppDirect('Lifetime PRO ($29.99)')}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-lg transition-all cursor-pointer whitespace-nowrap"
          >
            <MessageSquare size={15} />
            <span>Chat on WhatsApp (+91 7600477225)</span>
          </button>
        </div>

      </section>

      {/* 6. ARCHITECTURAL HIGHLIGHTS SECTION */}
      <section id="architecture" className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-xs font-bold text-cyan-400 uppercase tracking-widest">Engineering Excellence</span>
          <h3 className="text-3xl sm:text-4xl font-extrabold text-white font-display mt-2">
            Engineered for Maximum Precision &amp; Zero Stutter
          </h3>
          <p className="text-xs sm:text-sm text-slate-400 mt-2">
            Why CK Flow outperforms traditional software KVMs like Barrier, Synergy, and Mouse Without Borders
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          {/* Card 1 */}
          <div className="p-6 rounded-3xl bg-gradient-to-b from-white/[0.03] to-black/60 border border-white/10 hover:border-cyan-500/40 transition-all space-y-3">
            <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
              <Cpu size={24} />
            </div>
            <h4 className="text-base font-bold text-white">Infinite Delta Trapping Engine</h4>
            <p className="text-xs text-slate-400 leading-relaxed">
              Standard Windows hooks clamp mouse coordinates at the screen edge, freezing remote cursors. CK Flow centers host coordinates and continuously integrates physical deltas for unlimited, buttery-smooth movement.
            </p>
          </div>

          {/* Card 2 */}
          <div className="p-6 rounded-3xl bg-gradient-to-b from-white/[0.03] to-black/60 border border-white/10 hover:border-emerald-500/40 transition-all space-y-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
              <Activity size={24} />
            </div>
            <h4 className="text-base font-bold text-white">Direct Unicast UDP Mesh</h4>
            <p className="text-xs text-slate-400 leading-relaxed">
              Instead of flooding Wi-Fi routers with 255.255.255.255 broadcast frames, CK Flow maintains instant point-to-point UDP tunnels to active target PCs on port 52815 with sub-millisecond transmission.
            </p>
          </div>

          {/* Card 3 */}
          <div className="p-6 rounded-3xl bg-gradient-to-b from-white/[0.03] to-black/60 border border-white/10 hover:border-purple-500/40 transition-all space-y-3">
            <div className="w-12 h-12 rounded-2xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center text-purple-400">
              <ShieldCheck size={24} />
            </div>
            <h4 className="text-base font-bold text-white">Zero-Allocation Binary Protocol</h4>
            <p className="text-xs text-slate-400 leading-relaxed">
              Eliminates .NET Garbage Collection Gen 0 pauses during 1000Hz gaming mouse movement by utilizing fixed-size memory buffers with sequence tracking and microsecond timestamp telemetry.
            </p>
          </div>

        </div>
      </section>

      {/* 7. DOWNLOAD & SETUP SECTION */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="p-8 sm:p-10 rounded-3xl bg-gradient-to-r from-cyan-950/60 via-purple-950/50 to-black/90 border border-cyan-500/40 shadow-[0_0_60px_rgba(0,240,255,0.2)] flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center sm:text-left">
            <span className="px-2.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 font-mono text-xs font-bold">
              STANDALONE PORTABLE • ZERO INSTALL
            </span>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white font-display">
              Ready to link your PCs?
            </h3>
            <p className="text-xs text-slate-300 max-w-md">
              Download CK Flow on both computers, match Room ID &amp; Access Code, and glide!
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3">
            <a
              href="/downloads/CKFlow.exe"
              download="CKFlow.exe"
              rel="noopener noreferrer"
              className="px-6 py-3 text-xs font-bold text-black bg-gradient-to-r from-cyan-400 to-emerald-400 hover:from-cyan-300 hover:to-emerald-300 rounded-xl shadow-[0_0_20px_rgba(0,240,255,0.3)] hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
            >
              Download CK Flow (.exe)
            </a>

            <button
              onClick={() => openPurchase('LIFETIME')}
              className="px-5 py-3 text-xs font-bold text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 rounded-xl shadow-[0_0_20px_rgba(168,85,247,0.35)] transition-all cursor-pointer whitespace-nowrap"
            >
              Get Remote PRO ($29.99)
            </button>
          </div>
        </div>
      </section>

      {/* 8. FOOTER */}
      <footer className="border-t border-white/10 bg-black/60 py-10 px-4 sm:px-6 lg:px-8 text-center text-xs text-slate-500 space-y-4">
        <div className="flex items-center justify-center gap-6 flex-wrap text-slate-400">
          <button onClick={onSwitchToShare} className="hover:text-white transition-colors cursor-pointer">
            CK Share Universal Clipboard
          </button>
          <a href="https://github.com/chintankas1/ck-share" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
            GitHub Source Code
          </a>
          <button onClick={() => onOpenGuide('windows')} className="hover:text-white transition-colors cursor-pointer">
            Windows Setup Guide
          </button>
          <button onClick={() => openPurchase('LIFETIME')} className="hover:text-white transition-colors cursor-pointer text-cyan-400 font-bold">
            Buy PRO License
          </button>
        </div>
        <p>© 2026 CK Ecosystem • Founder: Chintan Kheradia • WhatsApp: +91 7600477225 • Email: chintankheradia123@gmail.com</p>
      </footer>

      {/* PURCHASE / ORDER MODAL */}
      <PurchaseFlowModal
        isOpen={isPurchaseModalOpen}
        onClose={() => setIsPurchaseModalOpen(false)}
        initialPlan={purchasePlan}
      />

    </div>
  );
};
